import React, { useState } from 'react';
import axios from 'axios';
import DoctorNavbar from './DocNav';
import { useAuth } from './context/AuthContext';
import Navbar from './Navbar';

const PredictForm = () => {
    const [symptoms, setSymptoms] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { currentUser } = useAuth();

    // Determine which navbar to use based on user role
    const userRole = currentUser?.role || 'patient';

    // Parse the text response from Python into structured data
    const parseResultText = (text) => {
        try {
            // Extract predicted disease and confidence
            const diseaseMatch = text.match(/You may have: (.*?) \(Confidence: ([\d.]+)\)/);
            const predictedDisease = diseaseMatch ? diseaseMatch[1] : 'Unknown';
            const confidence = diseaseMatch ? parseFloat(diseaseMatch[2]) : 0;

            // Extract top diseases and their confidences
            const topDiseasesSection = text.match(/Top predictions:\n([\s\S]*?)(?:\n\n|$)/);
            const topDiseases = [];

            if (topDiseasesSection) {
                const lines = topDiseasesSection[1].split('\n');
                lines.forEach(line => {
                    const match = line.match(/\d+\. (.*?): ([\d.]+)/);
                    if (match) {
                        topDiseases.push({
                            disease: match[1],
                            confidence: parseFloat(match[2])
                        });
                    }
                });
            }

            // Extract unrecognized symptoms
            const invalidSymptomsMatch = text.match(/Warning: Some symptoms were not recognized: (.*?)(?:\n|$)/);
            const invalidSymptoms = invalidSymptomsMatch
                ? invalidSymptomsMatch[1].split(', ').map(s => s.trim())
                : [];

            // Extract doctor information
            const doctorNameMatch = text.match(/Consult: (.*?)(?:\n|$)/);
            const doctorLinkMatch = text.match(/Visit: (.*?)(?:\n|$)/);

            const doctor = {};
            if (doctorNameMatch) doctor.name = doctorNameMatch[1];
            if (doctorLinkMatch) doctor.link = doctorLinkMatch[1];

            // Extract valid symptoms from the symptoms input (excluding invalid ones)
            const allSymptoms = symptoms.split(',').map(s => s.trim().toLowerCase());
            const validSymptoms = allSymptoms.filter(s => !invalidSymptoms.includes(s));

            return {
                predicted_disease: predictedDisease,
                confidence: confidence,
                top_diseases: topDiseases,
                valid_symptoms: validSymptoms,
                invalid_symptoms: invalidSymptoms,
                symptom_importance: {}, // Not available in the text format
                doctor: Object.keys(doctor).length > 0 ? doctor : null
            };
        } catch (error) {
            console.error('Error parsing result:', error);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!symptoms.trim()) {
            setError('Please enter at least one symptom.');
            return;
        }

        setIsLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await axios.post('http://localhost:5001/predict', {
                symptoms: symptoms.split(',').map(symptom => symptom.trim().toLowerCase())
            });

            // Parse the text response into structured data
            if (response.data && response.data.result) {
                const parsedResult = parseResultText(response.data.result);
                if (parsedResult) {
                    setResult(parsedResult);
                } else {
                    setError('Unable to parse the prediction results.');
                }
            } else {
                setError('Invalid response format from the server.');
            }
        } catch (err) {
            console.error('Prediction error:', err);
            setError(
                err.response?.data?.message ||
                'An error occurred while processing your request. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Add this helper function to determine color based on confidence
    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.8) return 'bg-green-200 text-green-800';
        if (confidence >= 0.4) return 'bg-blue-200 text-blue-800';
        return 'bg-yellow-200 text-yellow-800';
    };

    // Custom navbar based on role
    const RoleBasedNavbar = () => {
        return userRole === 'doctor' ? <DoctorNavbar /> : <Navbar />;
    };

    return (
        <>
            <RoleBasedNavbar />
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-200 p-4 pt-20">
                <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-3xl border border-gray-100">
                    <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">Health Symptom Analyzer</h1>
                    <p className="text-gray-600 mb-6 text-center">Enter your symptoms below to get a preliminary health assessment</p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="symptoms" className="block text-sm font-medium text-gray-700 mb-1">
                                Enter your symptoms (comma-separated):
                            </label>
                            <input
                                type="text"
                                id="symptoms"
                                value={symptoms}
                                onChange={(e) => setSymptoms(e.target.value)}
                                placeholder="e.g. fever, cough, headache"
                                disabled={isLoading}
                                required
                                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:text-gray-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">Separate multiple symptoms with commas</p>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-3 px-4 flex items-center justify-center font-semibold rounded-lg shadow 
                                      ${isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transform transition-transform hover:scale-[1.02]'} 
                                      text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : 'Analyze Symptoms'}
                        </button>
                    </form>

                    {isLoading && (
                        <div className="mt-8 text-center p-4">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                            <p className="mt-3 text-gray-700">Analyzing symptoms...</p>
                        </div>
                    )}

                    {result && !isLoading && (
                        <div className="mt-8 p-5 bg-blue-50 text-blue-800 border-l-4 border-blue-500 rounded-lg shadow-md transition-all duration-300 ease-in-out">
                            <h2 className="text-xl font-semibold flex items-center mb-4">
                                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Analysis Results
                            </h2>

                            {/* Primary Prediction */}
                            <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-blue-200">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-gray-800">Primary Assessment:</h3>
                                    <span className={`px-3 py-1 rounded-full ${getConfidenceColor(result.confidence)} text-sm font-semibold`}>
                                        {(result.confidence * 100).toFixed(1)}% confidence
                                    </span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-blue-900">{result.predicted_disease}</p>

                                {result.doctor && result.doctor.name && (
                                    <div className="mt-3 pt-3 border-t border-blue-100 text-gray-700">
                                        <p className="font-medium">Recommended specialist: {result.doctor.name}</p>
                                        {result.doctor.link && (
                                            <div>
                                                <p className="text-sm mt-1 break-words">{result.doctor.link}</p>
                                                <a
                                                    href={result.doctor.link.startsWith('http') ? result.doctor.link : `https://${result.doctor.link}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    <span>View provider details</span>
                                                    <svg className="ml-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Alternative Predictions */}
                            {result.top_diseases && result.top_diseases.length > 1 && (
                                <div className="mb-4">
                                    <h3 className="font-medium text-gray-700 mb-2">Alternative Assessments:</h3>
                                    <div className="grid gap-2">
                                        {result.top_diseases.slice(1).map((item, index) => (
                                            <div key={index} className="flex justify-between items-center p-3 bg-white rounded border border-gray-200">
                                                <span>{item.disease}</span>
                                                <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-700 text-xs font-medium">
                                                    {(item.confidence * 100).toFixed(1)}%
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Symptoms Analysis */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {/* Valid Symptoms */}
                                <div>
                                    <h3 className="font-medium text-gray-700 mb-2">Recognized Symptoms:</h3>
                                    <ul className="bg-white rounded-lg border border-gray-200 p-3">
                                        {result.valid_symptoms && result.valid_symptoms.map((symptom, index) => (
                                            <li key={index} className="py-1 flex items-center">
                                                <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                {symptom}
                                                {result.symptom_importance && result.symptom_importance[symptom] && (
                                                    <div className="ml-auto text-xs text-gray-500">
                                                        {(result.symptom_importance[symptom] * 100).toFixed(0)}% relevance
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Invalid Symptoms */}
                                {result.invalid_symptoms && result.invalid_symptoms.length > 0 && (
                                    <div>
                                        <h3 className="font-medium text-gray-700 mb-2">Unrecognized Symptoms:</h3>
                                        <ul className="bg-white rounded-lg border border-gray-200 p-3">
                                            {result.invalid_symptoms.map((symptom, index) => (
                                                <li key={index} className="py-1 flex items-center text-gray-500">
                                                    <svg className="h-4 w-4 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    {symptom}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            {/* Disclaimer */}
                            <div className="mt-6 text-xs text-gray-500 p-3 bg-gray-100 rounded-lg">
                                <p className="font-medium text-gray-700 mb-1">Important Disclaimer:</p>
                                <p>This tool provides preliminary health insights only and should not be considered a medical diagnosis.
                                    Always consult a healthcare professional for proper evaluation and treatment.</p>
                            </div>
                        </div>
                    )}

                    {error && !isLoading && (
                        <div className="mt-8 p-5 bg-red-50 text-red-800 border-l-4 border-red-500 rounded-lg shadow-md transition-all duration-300 ease-in-out">
                            <h2 className="text-xl font-semibold flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Error
                            </h2>
                            <div className="mt-3 text-red-700">{error}</div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default PredictForm;